/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ArrowLeft, Smartphone, Share, MoreVertical, PlusSquare, CheckCircle2, Download, WifiOff } from "lucide-react";

let deferredPrompt = null;
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => { e.preventDefault(); deferredPrompt = e; window.dispatchEvent(new Event("bc-install-ready")); });
}

export function detectPlatform() {
  const ua = navigator.userAgent || "";
  if (/iPhone|iPad|iPod/.test(ua)) return "ios";
  if (/Android/.test(ua)) return "android";
  return "desktop";
}

export const isStandalone = () => window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;

const Step = ({ n, icon: Icon, children, testid }) => (
  <li data-testid={testid} className="flex items-start gap-3 bg-white rounded-2xl border border-[#E4E0D5] p-4">
    <span className="w-8 h-8 rounded-full bg-[#2BA3A0] text-white font-bold grid place-items-center shrink-0">{n}</span>
    <div className="flex-1 text-[15px] text-[#0A1426] leading-snug">{children}</div>
    {Icon && <Icon size={22} className="text-[#0A1426]/60 shrink-0" />}
  </li>
);

export default function InstallGuidePage() {
  const navigate = useNavigate();
  const [platform, setPlatform] = useState(detectPlatform());
  const [canPrompt, setCanPrompt] = useState(Boolean(deferredPrompt));
  const [installed, setInstalled] = useState(isStandalone());
  useEffect(() => {
    const on = () => setCanPrompt(true);
    const done = () => setInstalled(true);
    window.addEventListener("bc-install-ready", on); window.addEventListener("appinstalled", done);
    return () => { window.removeEventListener("bc-install-ready", on); window.removeEventListener("appinstalled", done); };
  }, []);
  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") setInstalled(true);
    deferredPrompt = null; setCanPrompt(false);
  };

  return (
    <div className="bc-shell" data-testid="install-guide-page">
      <div className="px-5 pt-5 pb-3 flex items-center gap-3">
        <button data-testid="install-back" onClick={() => navigate(-1)} className="w-11 h-11 grid place-items-center rounded-full bg-white border border-[#E4E0D5] text-[#0A1426]"><ArrowLeft size={20} /></button>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-[#0A1426]/60 font-bold">Installation</div>
          <h1 className="font-[Fraunces] text-2xl font-bold text-[#0A1426]">Mettre Bombacare sur l'écran d'accueil</h1>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto bc-scroll px-5 pb-10 flex flex-col gap-4">
        <div className="rounded-3xl bg-[#0A1426] text-white p-4 flex items-center gap-3">
          <WifiOff size={26} className="text-[#D4AF5A]" />
          <p className="text-[13px] text-white/85">Installée, l'application s'ouvre comme une vraie app, <b>même sans internet</b>, avec vos exercices, illustrations et données.</p>
        </div>

        {installed && (
          <div data-testid="install-done" className="rounded-2xl bg-[#5CB85C]/15 text-[#2d7a2d] p-4 flex items-center gap-2 font-bold"><CheckCircle2 size={20} /> Bombacare est déjà installée sur cet appareil.</div>
        )}

        <div className="grid grid-cols-3 gap-2">
          {[["android", "Android"], ["ios", "iPhone / iPad"], ["desktop", "Ordinateur"]].map(([k, l]) => (
            <button key={k} data-testid={`install-tab-${k}`} onClick={() => setPlatform(k)} className={`h-11 rounded-full text-[12px] font-bold ${platform === k ? "bg-[#0A1426] text-white" : "bg-white border border-[#E4E0D5] text-[#0A1426]"}`}>{l}</button>
          ))}
        </div>

        {platform === "android" && (
          <ol className="flex flex-col gap-2" data-testid="install-steps-android">
            {canPrompt && !installed && (
              <Button data-testid="install-now-btn" onClick={install} className="h-14 rounded-full bg-[#2BA3A0] text-white font-bold text-base"><Download size={20} className="mr-2" /> Installer maintenant</Button>
            )}
            <Step n={1} icon={MoreVertical} testid="install-android-1">Ouvrez Bombacare dans <b>Chrome</b>, puis touchez les <b>trois points ⋮</b> en haut à droite.</Step>
            <Step n={2} icon={PlusSquare} testid="install-android-2">Choisissez <b>« Ajouter à l'écran d'accueil »</b> (ou « Installer l'application »).</Step>
            <Step n={3} icon={CheckCircle2} testid="install-android-3">Confirmez avec <b>« Ajouter »</b>. L'icône feuille Bombacare apparaît parmi vos applications.</Step>
          </ol>
        )}
        {platform === "ios" && (
          <ol className="flex flex-col gap-2" data-testid="install-steps-ios">
            <Step n={1} icon={Share} testid="install-ios-1">Ouvrez Bombacare dans <b>Safari</b>, puis touchez le bouton <b>Partager</b> (carré avec une flèche vers le haut) en bas de l'écran.</Step>
            <Step n={2} icon={PlusSquare} testid="install-ios-2">Faites défiler et choisissez <b>« Sur l'écran d'accueil »</b>.</Step>
            <Step n={3} icon={CheckCircle2} testid="install-ios-3">Touchez <b>« Ajouter »</b> en haut à droite. Bombacare s'ouvre désormais en plein écran depuis votre écran d'accueil.</Step>
            <li className="text-[12px] text-[#0A1426]/60 px-1">Sur iPhone, l'installation n'est possible que depuis Safari (pas depuis Chrome ni Facebook).</li>
          </ol>
        )}
        {platform === "desktop" && (
          <ol className="flex flex-col gap-2" data-testid="install-steps-desktop">
            {canPrompt && !installed && (
              <Button data-testid="install-now-btn" onClick={install} className="h-14 rounded-full bg-[#2BA3A0] text-white font-bold text-base"><Download size={20} className="mr-2" /> Installer maintenant</Button>
            )}
            <Step n={1} icon={Download} testid="install-desktop-1">Dans Chrome ou Edge, cliquez sur l'icône <b>Installer</b> à droite de la barre d'adresse.</Step>
            <Step n={2} icon={CheckCircle2} testid="install-desktop-2">Confirmez : Bombacare s'ouvre dans sa propre fenêtre.</Step>
          </ol>
        )}

        <div className="bg-[#D9534F] rounded-2xl p-4 text-white flex items-start gap-3" data-testid="install-shortcut-card">
          <img src="/icon-urgence.svg" alt="" className="w-12 h-12 rounded-xl shrink-0" />
          <div className="flex-1">
            <div className="font-[Fraunces] text-base font-bold leading-tight">Raccourci « Je suis inquiet »</div>
            <p className="text-[12px] text-white/90 mt-1 leading-snug">
              {platform === "ios"
                ? "Sur iPhone : ouvrez Bombacare, allez dans Alertes urgentes → « Je suis inquiet », puis Partager → « Sur l'écran d'accueil » : l'icône rouge ouvrira directement l'écran d'urgence."
                : "Une fois Bombacare installée : appuyez longuement sur l'icône de l'app → le menu propose « Je suis inquiet — urgence » (icône rouge). Vous pouvez glisser ce raccourci sur l'écran d'accueil pour y accéder en un geste."}
            </p>
            <Button data-testid="install-shortcut-open" onClick={() => navigate("/app/urgence")} variant="outline" className="h-10 mt-3 rounded-full bg-white text-[#D9534F] border-white font-bold">Ouvrir l'écran d'urgence</Button>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-[#E4E0D5] p-4 flex items-start gap-3">
          <Smartphone size={22} className="text-[#2BA3A0] shrink-0" />
          <p className="text-[13px] text-[#0A1426]/80">Pensez à activer « Télécharger les vidéos en Wi-Fi » dans votre Profil pour disposer des démonstrations hors-ligne.</p>
        </div>
      </div>
    </div>
  );
}
